import React from 'react';
import { UploadProgress } from '../../hooks/useAsyncUpload';
import './UploadProgressIndicator.css';

interface UploadProgressIndicatorProps {
  upload: UploadProgress;
  onRemove?: (fileId: string) => void;
  compact?: boolean;
}

export const UploadProgressIndicator: React.FC<UploadProgressIndicatorProps> = ({
  upload,
  onRemove,
  compact = false
}) => {
  const getStatusText = () => {
    // If there's a backend message, show it for both processing and queued status
    if (upload.message && (upload.status === 'processing' || upload.status === 'queued')) {
      return upload.message;
    }
    
    switch (upload.status) {
      case 'uploading':
        return `Uploading... ${upload.progress}%`;
      case 'queued':
        return `Queued (${upload.progress}%)`;
      case 'processing':
        return `Processing... ${upload.progress}%`;
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = () => {
    switch (upload.status) {
      case 'uploading':
      case 'queued':
      case 'processing':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (compact) {
    return (
      <div className={`upload-progress-compact upload-progress-${upload.status}`}>
        <div className="upload-progress-info">
          <span className="upload-filename">{upload.filename}</span>
          <span className="upload-status">{getStatusText()}</span>
        </div>
        <div className="upload-progress-bar">
          <div 
            className="upload-progress-fill" 
            style={{ 
              width: `${upload.progress}%`,
              backgroundColor: getStatusColor() === 'blue' ? '#3b82f6' : 
                             getStatusColor() === 'green' ? '#10b981' : '#ef4444'
            }} 
          />
        </div>
        {upload.error && (
          <div className="upload-error">{upload.error}</div>
        )}
      </div>
    );
  }

  return (
    <div className={`upload-progress upload-progress-${upload.status}`}>
      <div className="upload-progress-header">
        <div className="upload-progress-info">
          <span className="upload-filename">{upload.filename}</span>
          <span className="upload-status">{getStatusText()}</span>
        </div>
        {onRemove && upload.status !== 'processing' && (
          <button 
            className="upload-remove-btn"
            onClick={() => onRemove(upload.fileId)}
            aria-label="Remove upload"
          >
            ×
          </button>
        )}
      </div>
      
      <div className="upload-progress-bar">
        <div 
          className="upload-progress-fill" 
          style={{ 
            width: `${upload.progress}%`,
            backgroundColor: getStatusColor() === 'blue' ? '#3b82f6' : 
                           getStatusColor() === 'green' ? '#10b981' : '#ef4444'
          }} 
        />
      </div>
      
      <div className="upload-progress-text">
        {upload.progress}%
      </div>
      
      {upload.error && (
        <div className="upload-error">
          Error: {upload.error}
        </div>
      )}
    </div>
  );
};